// Lab settings (a single 'default' row): case/invoice numbering config + lab identity. The
// numbering fields were hardcoded in cases/repo.ts; they live here now so the admin can edit them
// and `resetInterval` is finally honored.
import { z } from 'zod';
import type { AlignerPricing } from '../aligner-pricing.js';

export const RESET_INTERVALS = ['never', 'yearly', 'monthly'] as const;
export type ResetInterval = (typeof RESET_INTERVALS)[number];
export const RESET_INTERVAL_LABELS: Record<ResetInterval, string> = {
  never: 'Never (continuous)',
  yearly: 'Yearly',
  monthly: 'Monthly',
};

export interface LabSettings {
  numberPrefix: string;
  includeYear: boolean;
  resetInterval: ResetInterval;
  invoicePrefix: string;
  labName: string;
  labAddress: string | null;
  labPhone: string | null;
  labEmail: string | null;
  labWhatsapp: string | null;
  /** What a clinic pays per aligner tray, tiered by tray count. See shared/aligner-pricing. */
  alignerPricing: AlignerPricing;
  updatedAt: string;
}

const optText = (max: number) => z.string().trim().max(max).nullish();

export const updateSettingsSchema = z
  .object({
    numberPrefix: z.string().trim().min(1).max(12).optional(),
    includeYear: z.boolean().optional(),
    resetInterval: z.enum(RESET_INTERVALS).optional(),
    invoicePrefix: z.string().trim().min(1).max(12).optional(),
    labName: z.string().trim().min(1).max(160).optional(),
    labAddress: optText(300),
    labPhone: optText(40),
    labEmail: z.string().trim().email().max(160).nullish(),
    labWhatsapp: optText(40),
    // Aligner pricing. Whole rupees; the threshold is a tray count. Zero is allowed for a rate
    // (a lab may give bulk trays away) but not a negative number.
    alignerThreshold: z.number().int().min(0).max(1000).optional(),
    alignerRateAtOrBelow: z.number().int().min(0).max(10_000_000).optional(),
    alignerRateAbove: z.number().int().min(0).max(10_000_000).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export type UpdateSettingsRequest = z.infer<typeof updateSettingsSchema>;

/**
 * The lab's master key for clinic portals.
 *
 * Only ever reported as "is one set, and since when" — the password itself is stored as a bcrypt
 * hash and never leaves the server in any form.
 */
export interface ClinicMasterStatus {
  set: boolean;
  setAt: string | null;
}

export const clinicMasterSchema = z.object({
  /** Empty removes it. Otherwise long enough to be worth having, since it opens every clinic. */
  password: z.string().max(200),
});
